import {
    Component,
    Input,
    Output,
    EventEmitter,
    OnInit,
    OnChanges,
    TemplateRef,
    ViewChild,
    ContentChildren,
    QueryList,
    AfterContentInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    TrackByFunction,
    computed,
    signal,
    input,
    output,
    effect,
    untracked,
} from '@angular/core';
import { lastValueFrom } from 'rxjs';
import {
    trigger,
    state,
    style,
    transition,
    animate,
} from '@angular/animations';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import {
    TableColumn,
    TableAction,
    TableConfiguration,
    SortEvent,
    ActionEvent,
    SelectionEvent,
    PaginationConfig,
    FilterConfig,
    SortConfig,
} from '@app/shared/models/generic-table.interface';
import { TableRowData } from '@app/shared/models/table-data-types';
import {
    getSelectedItems,
    toggleSelection,
    setSelection,
    clearSelection,
    isItemSelected,
} from '@app/stores/selectionStore';

@Component({
    selector: 'app-generic-table',
    templateUrl: './generic-table.component.html',
    styleUrls: ['./generic-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
    animations: [
        trigger('expandCollapse', [
            state('expanded', style({ height: '*', opacity: 1 })),
            state('collapsed', style({ height: '0px', opacity: 0 })),
            transition('expanded <=> collapsed', [
                animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'),
            ]),
        ]),
    ],
})
export class GenericTableComponent<T extends TableRowData = TableRowData>
    implements OnInit, AfterContentInit
{
    @ViewChild(MatSort) sort: MatSort;
    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ContentChildren(TemplateRef) templates: QueryList<TemplateRef<any>>;

    // Data inputs - Signal-based
    data = input<T[]>([]);
    columns = input<TableColumn<T>[]>([]);
    config = input<TableConfiguration<T>>({ columns: [] });

    // State inputs - Signal-based
    loading = input<boolean>(false);
    error = input<string | null>(null);
    totalItems = input<number>(0);

    // Configuration inputs - Signal-based
    pagination = input<PaginationConfig | null>(null);
    sorting = input<SortConfig | null>(null);
    filtering = input<FilterConfig | null>(null);
    actions = input<TableAction<T>[]>([]);

    // Feature flags
    @Input() selectable = false;
    @Input() sortable = true;
    @Input() filterable = false;
    @Input() paginated = false;
    @Input() expandable = false;
    @Input() showActions = true;
    @Input() showFilter = true;
    @Input() showPagination = true;
    @Input() showSelection = true;

    // Styling
    @Input() tableClass = '';
    @Input() maxHeight: string | null = null;
    @Input() emptyStateMessage = 'GENERAL.NO-DATA';
    @Input() loadingMessage = 'GENERAL.LOADING';

    // Events - Signal-based outputs
    sortChange = output<SortEvent>();
    pageChange = output<PageEvent>();
    actionClick = output<ActionEvent<T>>();
    selectionChange = output<SelectionEvent<T>>();
    filterChange = output<any>();
    refresh = output<void>();
    rowExpand = output<{ row: T; expanded: boolean }>();

    // Signal to track selection changes for reactivity
    private selectionChangeSignal = signal<number>(0);
    expandedRows = new Set<any>();
    expandedRowsData = new Map<any, any[]>();
    expandedRowsLoading = new Set<any>();
    currentSort = signal<{ key: string; direction: 'asc' | 'desc' | '' }>({
        key: '',
        direction: '',
    });

    // Computed properties - Signal-based
    dataSource = computed(() => {
        const source = new MatTableDataSource<T>(this.data());
        return source;
    });

    visibleColumns = computed(() => {
        const cols =
            this.columns().length > 0
                ? this.columns()
                : this.config()?.columns || [];
        return cols
            .filter(col => {
                if (col.visible === false) return false;
                if (typeof col.visible === 'function') {
                    return col.visible(); // Call the function to determine visibility
                }
                return col.visible == null || col.visible === true; // Default to true if not specified
            })
            .sort((a, b) => (a.order || 0) - (b.order || 0));
    });

    displayedColumns = computed(() => {
        // Get all visible columns and check for system columns
        const visibleCols = this.visibleColumns();
        const columns: string[] = [];

        // Add columns in the order they appear in the configuration
        for (const col of visibleCols) {
            const key = String(col.key);

            // Skip expandedDetail from main columns - it's only for expanded rows
            if (key !== 'expandedDetail') {
                columns.push(key);
            }
        }

        // Check if we should add actions column automatically
        // Only add if not already present and there are actions available
        const hasActionsColumn = columns.includes('actions');
        const mainTableActions = this.getMainTableActions();

        if (!hasActionsColumn && mainTableActions.length > 0) {
            columns.push('actions');
        }

        return columns;
    });

    hasSelection = computed(() => {
        // Depend on the selection change signal to ensure reactivity
        this.selectionChangeSignal();
        const selectedItems = getSelectedItems();
        return selectedItems.length > 0;
    });

    isAllSelected = computed(() => {
        // Depend on the selection change signal to ensure reactivity
        this.selectionChangeSignal();
        const selectableRows = this.getSelectableRows();
        const numSelectableRows = selectableRows.length;

        if (numSelectableRows === 0) {
            return false;
        }

        const selectedItems = getSelectedItems();

        // Use key-based comparison to check if all selectable rows are selected
        const selectedKeys = new Set(
            selectedItems
                .map(item => {
                    return '_key' in item && typeof item._key === 'string'
                        ? item._key
                        : null;
                })
                .filter(key => key !== null)
        );

        const allSelectableSelected = selectableRows.every(row => {
            if ('_key' in row && typeof row._key === 'string') {
                return selectedKeys.has(row._key);
            }
            // Fallback: check if item is in selected items
            return selectedItems.some(selected => selected === row);
        });

        return allSelectableSelected;
    });

    // Template references
    templateMap = new Map<string, TemplateRef<any>>();

    // Track by function for performance
    trackByFunction: TrackByFunction<T> = (index: number, item: T) => {
        const config = this.config();
        return config?.trackBy ? config.trackBy(index, item) : index;
    };

    // Track by function for columns
    trackByColumn: TrackByFunction<TableColumn<T>> = (
        index: number,
        column: TableColumn<T>
    ) => {
        return column.key || index;
    };

    constructor(private cdr: ChangeDetectorRef) {
        // Effect to track global selection changes and emit selection events
        effect(() => {
            const globalSelection = getSelectedItems();
            // Trigger the selection change signal to update computed signals
            this.selectionChangeSignal.update(val => val + 1);

            // Emit selection change event
            this.selectionChange.emit({
                selected: globalSelection as T[],
                all: this.dataSource().data,
                type:
                    globalSelection.length === 0
                        ? 'none'
                        : globalSelection.length ===
                            this.dataSource().data.length
                          ? 'all'
                          : 'single',
            });
        });

        // Effect for data source updates
        effect(() => {
            this.data(); // Track data changes
            untracked(() => {
                // Clear expanded row state when data changes to prevent stale data
                this.expandedRows.clear();
                this.expandedRowsData.clear();
                this.expandedRowsLoading.clear();

                this.updateDataSourceBinding();
            });
        });

        // Effect for sorting updates
        effect(() => {
            const sortConfig = this.sorting();
            if (sortConfig) {
                untracked(() => {
                    this.currentSort.set({
                        key: sortConfig.sortBy,
                        direction: sortConfig.sortDirection,
                    });
                });
            }
        });
    }

    ngOnInit(): void {
        this.initializeTable();
    }

    ngAfterContentInit(): void {
        this.initializeTemplates();
    }

    private initializeTable(): void {
        this.updateDataSourceBinding();
    }

    private updateDataSourceBinding(): void {
        const dataSource = this.dataSource();

        if (this.sort && this.sortable) {
            dataSource.sort = this.sort;
        }

        if (this.paginator && this.paginated) {
            dataSource.paginator = this.paginator;
        }
    }

    private initializeTemplates(): void {
        this.templates.forEach((template, index) => {
            this.templateMap.set(`template-${index}`, template);
        });
    }

    // Sorting methods
    onSort(column: TableColumn<T>): void {
        console.log('Sorting column: ', column);
        if (!column.sortable) return;

        const key = String(column.key);
        const currentSort = this.currentSort();

        let direction: 'asc' | 'desc' | '' = 'asc';

        if (currentSort.key === key) {
            direction =
                currentSort.direction === 'asc'
                    ? 'desc'
                    : currentSort.direction === 'desc'
                      ? ''
                      : 'asc';
        }

        this.currentSort.set({ key, direction });
        this.sortChange.emit({ key, direction });
    }

    getSortDirection(column: TableColumn<T>): 'asc' | 'desc' | '' {
        const currentSort = this.currentSort();
        return currentSort.key === String(column.key)
            ? currentSort.direction
            : '';
    }

    // Selection methods
    toggleAllSelection(): void {
        const selectableRows = this.getSelectableRows();
        const selectableRowsCount = selectableRows.length;

        if (selectableRowsCount === 0) {
            return;
        }

        const selectedItems = getSelectedItems();

        // Use key-based comparison to determine if all are selected
        const selectedKeys = new Set(
            selectedItems
                .map(item => {
                    return '_key' in item && typeof item._key === 'string'
                        ? item._key
                        : null;
                })
                .filter(key => key !== null)
        );

        const allSelectableSelected = selectableRows.every(row => {
            if ('_key' in row && typeof row._key === 'string') {
                return selectedKeys.has(row._key);
            }
            // Fallback: check if item is in selected items
            return selectedItems.some(selected => selected === row);
        });

        if (allSelectableSelected) {
            // Clear all selections - find items to remove from current selection
            const itemsToKeep = selectedItems.filter(selectedItem => {
                if (
                    '_key' in selectedItem &&
                    typeof selectedItem._key === 'string'
                ) {
                    return !selectableRows.some(
                        row => '_key' in row && row._key === selectedItem._key
                    );
                }
                return !selectableRows.includes(selectedItem as T);
            });
            setSelection(itemsToKeep);
        } else {
            // Add all selectable rows that aren't already selected
            const currentSelection = [...selectedItems];
            selectableRows.forEach(row => {
                if ('_key' in row && typeof row._key === 'string') {
                    const isAlreadySelected = selectedItems.some(
                        selectedItem =>
                            '_key' in selectedItem &&
                            selectedItem._key === row._key
                    );
                    if (!isAlreadySelected) {
                        currentSelection.push(row as any);
                    }
                } else {
                    // Fallback to object reference for items without _key
                    if (!selectedItems.includes(row as any)) {
                        currentSelection.push(row as any);
                    }
                }
            });
            setSelection(currentSelection);
        }
    }

    toggleRowSelection(row: T): void {
        toggleSelection(row as any);
    }

    isRowSelected(row: T): boolean {
        return isItemSelected(row as any);
    }

    getSelectableRows(): T[] {
        const selectColumn = this.visibleColumns().find(
            col => col.key === 'select'
        );
        if (!selectColumn || !selectColumn.selectableCondition) {
            return this.dataSource().data;
        }
        return this.dataSource().data.filter(row =>
            selectColumn.selectableCondition!(row)
        );
    }

    // Action methods
    onActionClick(action: TableAction<T>, row: T, index: number): void {
        if (action.disabled && action.disabled(row)) return;

        this.actionClick.emit({
            actionId: action.id,
            row,
            index,
        });

        if (action.handler) {
            action.handler(row);
        }
    }

    isActionVisible(action: TableAction<T>, row: T): boolean {
        return action.visible ? action.visible(row) : true;
    }

    isActionDisabled(action: TableAction<T>, row: T): boolean {
        return action.disabled ? action.disabled(row) : false;
    }

    // Pagination methods
    onPageChange(event: PageEvent): void {
        this.pageChange.emit(event);
    }

    // Utility methods
    getCellValue(row: T, column: TableColumn<T>): any {
        if (!row || !column) return '';
        return row[column.key];
    }

    getFormattedValue(row: T, column: TableColumn<T>): string {
        const value = this.getCellValue(row, column);

        if (column.formatter) {
            return column.formatter(value, row);
        }

        if (column.pipe) {
            // For pipes, we'll handle them in the template
            return value?.toString() || '';
        }

        return value?.toString() || '';
    }

    getColumnTemplate(column: TableColumn<T>): TemplateRef<any> | null {
        return column.template || null;
    }

    getComponentInputs(row: T, column: TableColumn<T>): Record<string, any> {
        const value = this.getCellValue(row, column);
        const defaultInputs = {
            row,
            column,
            value,
            index: this.dataSource().data.indexOf(row),
        };

        if (column.componentInputs) {
            return {
                ...defaultInputs,
                ...column.componentInputs(row, value, undefined),
            };
        }

        return defaultInputs;
    }

    getExpandedComponentInputs(
        item: any,
        column: TableColumn<any>,
        parentRow: any
    ): Record<string, any> {
        const value = item[column.key];
        const defaultInputs = {
            row: item,
            column,
            value,
            index: this.getExpandedRowData(parentRow).indexOf(item),
            parentRow,
        };

        if (column.componentInputs) {
            return {
                ...defaultInputs,
                ...column.componentInputs(item, value, parentRow),
            };
        }

        return defaultInputs;
    }

    getRowClass(row: T): string {
        const config = this.config();
        return config?.rowClass ? config.rowClass(row) : '';
    }

    // Public methods for external control
    clearSelection(): void {
        clearSelection();
    }

    selectAll(): void {
        const allRows = this.dataSource().data;
        setSelection(allRows as any[]);
    }

    getSelectedRows(): T[] {
        return getSelectedItems() as T[];
    }

    refreshData(): void {
        this.refresh.emit();
    }

    // Error handling methods
    getErrorMessage(): string {
        const error = this.error();
        if (!error) return '';

        // Check if error contains a request ID (format: message|requestId)
        const parts = error.split('|');
        return parts[0] || error;
    }

    getErrorCode(): string | null {
        const error = this.error();
        if (!error) return null;

        // Check if error contains a request ID (format: message|requestId)
        const parts = error.split('|');
        return parts.length > 1 ? parts[1] : null;
    }

    getErrorDescriptionKey(): string {
        return this.getErrorCode()
            ? 'GENERAL.ERROR-DESCRIPTION-WITH-CODE'
            : 'GENERAL.ERROR-DESCRIPTION';
    }

    // Expandable row methods
    toggleRowExpansion(row: T): void {
        const rowId = this.getRowId(row);
        const isExpanded = this.expandedRows.has(rowId);

        if (isExpanded) {
            this.expandedRows.delete(rowId);
            this.expandedRowsData.delete(rowId);
            this.expandedRowsLoading.delete(rowId);
        } else {
            this.expandedRows.add(rowId);
            this.loadExpandedRowData(row, rowId);
        }

        // Trigger change detection since we're using OnPush
        this.cdr.detectChanges();

        this.rowExpand.emit({ row, expanded: !isExpanded });
    }

    isRowExpanded(row: T): boolean {
        const rowId = this.getRowId(row);
        return this.expandedRows.has(rowId);
    }

    canRowExpand(row: T): boolean {
        const config = this.config();
        if (config?.expandedRowConfig?.expandableCondition) {
            return config.expandedRowConfig.expandableCondition(row);
        }
        return true;
    }

    getExpandedData(row: T): any {
        const config = this.config();
        if (config?.expandedRowConfig?.expandedData) {
            return config.expandedRowConfig.expandedData(row);
        }
        return row;
    }

    private getRowId(row: T): any {
        // Use trackBy function if available, otherwise use the row object itself
        const trackBy = this.config()?.trackBy;
        if (trackBy) {
            return trackBy(0, row);
        }
        // Fallback to a unique identifier if available, otherwise use the row object
        if (row && typeof row === 'object' && '_key' in row) {
            return (row as any)._key;
        }
        if (row && typeof row === 'object' && 'id' in row) {
            return (row as any).id;
        }
        return row;
    }

    // Utility method to convert key to string
    getColumnKey(column: TableColumn<T>): string {
        return String(column.key);
    }

    // Helper methods to check for system columns
    hasSelectColumn(): boolean {
        return this.visibleColumns().some(col => col.key === 'select');
    }

    hasActionsColumn(): boolean {
        return this.visibleColumns().some(col => col.key === 'actions');
    }

    // Expanded row data loading methods
    private async loadExpandedRowData(row: T, rowId: any): Promise<void> {
        const config = this.config();
        const tableConfig = config?.expandedRowConfig?.tableConfig;

        if (!tableConfig?.apiConfig?.loadData) {
            return;
        }

        this.expandedRowsLoading.add(rowId);
        this.cdr.detectChanges();

        try {
            const orgSiteKey = tableConfig.apiConfig.organizationSiteKeyResolver
                ? tableConfig.apiConfig.organizationSiteKeyResolver(row)
                : this.getDefaultOrganizationSiteKey(row);

            const customParams = tableConfig.apiConfig.customParams
                ? tableConfig.apiConfig.customParams(row)
                : {};

            const result = tableConfig.apiConfig.loadData(
                row,
                orgSiteKey,
                customParams
            );
            // Handle both Observable and Promise returns
            const data =
                result instanceof Promise
                    ? await result
                    : await lastValueFrom(result);
            this.expandedRowsData.set(rowId, Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error loading expanded row data:', error);
            this.expandedRowsData.set(rowId, []);
        } finally {
            this.expandedRowsLoading.delete(rowId);
            this.cdr.detectChanges();
        }
    }

    private getDefaultOrganizationSiteKey(row: T): string {
        // Try to extract organization site key from common properties
        if (row && typeof row === 'object') {
            const rowObj = row as any;
            return (
                rowObj.organizationSiteKey ||
                rowObj.orgSiteKey ||
                rowObj.siteKey ||
                ''
            );
        }
        return '';
    }

    getExpandedRowData(row: T): any[] {
        const rowId = this.getRowId(row);
        return this.expandedRowsData.get(rowId) || [];
    }

    isExpandedRowLoading(row: T): boolean {
        const rowId = this.getRowId(row);
        return this.expandedRowsLoading.has(rowId);
    }

    getExpandedRowColumns(): TableColumn<any>[] {
        const config = this.config();
        return config?.expandedRowConfig?.tableConfig?.columns || [];
    }

    getExpandedRowActions(): TableAction<any>[] {
        const config = this.config();
        const tableConfig = config?.expandedRowConfig?.tableConfig;

        // Check for actions in multiple locations
        const directActions = tableConfig?.actions || [];

        // Return direct actions only (actionConfig removed)
        return directActions;
    }

    getExpandedRowGridConfig() {
        const config = this.config();
        return config?.expandedRowConfig?.tableConfig?.gridConfig;
    }

    getExpandedRowDisplayedColumns(): string[] {
        const columns = this.getExpandedRowColumns().map(col =>
            this.getColumnKey(col)
        );
        const actions = this.getExpandedRowActions();
        return actions.length > 0 ? columns.concat(['actions']) : columns;
    }

    // Main table actions - from actionConfig at the root level of the passed config
    getMainTableActions(): TableAction<T>[] {
        const config = this.config();

        // Get actions directly from config (actionConfig removed)
        const directActions = config?.actions || [];

        return directActions;
    }

    // Pagination options for the TablePaginationComponent
    paginationOptions(): number[] {
        const config = this.config();
        return config?.paginationOptions || [10, 20, 50, 100];
    }
}
